import { z } from 'zod';
import { financingStageOrder } from '../state-machines/financing.js';
/**
 * Enums, labels, tones and request schemas of the financings module (Finanzierungen 01–11, Finanzierer
 * 03/09). The server sends the labels of its rows; these constants serve controls that offer a choice
 * before a row exists (create dialog, stage dialog, lost dialog). German strings verbatim from the screens.
 */
export const financingTypes = ['annuity', 'kfw', 'follow_on', 'forward'];
/** The six stage keys as a tuple for request/response schemas (order = `financingStageOrder`). */
export const financingStageKeys = [
    'documents',
    'requested',
    'offers',
    'commitment',
    'signed',
    'disbursed',
];
/** „Art" as shown in lists, the record title and the board (Finanzierungen 01, 06, 07). */
export const financingTypeLabels = {
    annuity: 'Annuitätendarlehen',
    kfw: 'KfW',
    follow_on: 'Anschlussfinanzierung',
    forward: 'Forward',
};
/** Segments of the „Art" control in the create dialogs (Finanzierungen 04/05). */
export const financingTypeOptionLabels = {
    annuity: 'Annuitätendarlehen',
    kfw: 'KfW',
    follow_on: 'Anschluss',
    forward: 'Forward',
};
/** Status pill colors per stage (Finanzierungen A2). */
export const financingStageTones = {
    documents: 'neutral',
    requested: 'info',
    offers: 'info',
    commitment: 'accent',
    signed: 'success',
    disbursed: 'success',
};
export const FINANCING_LOST_LABEL = 'Verloren';
export const FINANCING_LOST_TONE = 'danger';
/** List filter keys (Finanzierungen 01/02): the six stages, „Verloren" and the orthogonal „ohne Deal". */
export const financingListFilters = [...financingStageOrder, 'lost', 'without_deal'];
/** Display number „FIN-0142" (P-34). */
export function formatFinancingNumber(number) {
    return `FIN-${String(number).padStart(4, '0')}`;
}
const cents = z.number().int().min(0);
const years = z.number().min(0).max(50);
const dayString = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const ratePct = z.number().min(0).max(100);
export const financingPropertyInputSchema = z.object({
    type: z.string().trim().max(200).optional(),
    address: z.string().trim().max(500).optional(),
    remainingDebtCents: cents.nullable().optional(),
    currentBank: z.string().trim().max(200).nullable().optional(),
});
/** POST /financings (Finanzierungen 04, 05, 10). */
export const createFinancingBodySchema = z.object({
    contactId: z.string().uuid(),
    dealId: z.string().uuid().nullable().optional(),
    type: z.enum(financingTypes),
    amountCents: cents,
    amountSource: z.string().trim().max(100).nullable().optional(),
    fixedInterestYears: years.nullable().optional(),
    fixedInterestEndsOn: dayString.nullable().optional(),
    property: financingPropertyInputSchema.optional(),
    financierId: z.string().uuid().nullable().optional(),
    commissionRatePct: ratePct.nullable().optional(),
});
/** PATCH /financings/:id. */
export const patchFinancingBodySchema = z.object({
    type: z.enum(financingTypes).optional(),
    amountCents: cents.optional(),
    fixedInterestYears: years.nullable().optional(),
    fixedInterestEndsOn: dayString.nullable().optional(),
    property: financingPropertyInputSchema.optional(),
    commissionRatePct: ratePct.optional(),
});
/** POST /financings/:id/stage (Stufe wechseln). */
export const financingStageBodySchema = z.object({
    to: z.enum(financingStageKeys),
    reason: z.string().trim().max(2000).optional(),
    disbursedOn: dayString.optional(),
});
/** POST /financings/:id/lose (Finanzierungen 11). */
export const loseFinancingBodySchema = z.object({
    lostReasonId: z.string().uuid(),
    note: z.string().trim().max(2000).optional(),
});
/** POST /financings/:id/not-financeable (Finanzierer 08). */
export const notFinanceableBodySchema = z.object({ note: z.string().trim().max(2000).optional() });
/** POST /financings/:id/reopen. */
export const reopenFinancingBodySchema = z.object({ reason: z.string().trim().min(1).max(2000) });
/** POST /financings/:id/financier („Neu zuweisen" / „Vorschlagen"). */
export const financingFinancierBodySchema = z.object({ financierId: z.string().uuid() });
/** POST /financings/:id/assignment/decide. */
export const financingAssignmentDecideBodySchema = z.object({
    approvalId: z.string().uuid(),
    decision: z.enum(['approved', 'rejected']),
    reason: z.string().trim().max(2000).optional(),
});
/** POST /financings/:id/link-deal. */
export const linkFinancingDealBodySchema = z.object({ dealId: z.string().uuid() });
//# sourceMappingURL=financings.js.map